import client from './client';

export const helpdeskApi = {
  // ==================== APPS ====================
  async getApps() {
    const { data } = await client.get('/api/helpdesk/apps');
    return data;
  },

  async createApp(appData) {
    const { data } = await client.post('/api/helpdesk/apps', appData);
    return data;
  },

  async getApp(id) {
    const { data } = await client.get(`/api/helpdesk/apps/${id}`);
    return data;
  },

  async updateApp(id, appData) {
    const { data } = await client.patch(`/api/helpdesk/apps/${id}`, appData);
    return data;
  },

  async archiveApp(id) {
    const { data } = await client.delete(`/api/helpdesk/apps/${id}`);
    return data;
  },

  async getAppStats(id) {
    const { data } = await client.get(`/api/helpdesk/apps/${id}/stats`);
    return data;
  },

  // ==================== NOTIFICATIONS ====================
  async getNotifications() {
    const { data } = await client.get('/api/helpdesk/apps/notifications');
    return data;
  },

  async markAllNotificationsRead() {
    const { data } = await client.post('/api/helpdesk/apps/notifications/read-all');
    return data;
  },

  async markTicketRead(appId, ticketNumber) {
    const { data } = await client.post(`/api/helpdesk/apps/${appId}/tickets/${ticketNumber}/read`);
    return data;
  },

  // ==================== ALL TICKETS ====================
  async getAllTickets(params = {}) {
    const { data } = await client.get('/api/helpdesk/apps/all-tickets', { params });
    return data;
  },

  // ==================== TICKETS ====================
  async createTicket(appId, ticketData) {
    const { data } = await client.post(`/api/helpdesk/apps/${appId}/tickets`, ticketData);
    return data;
  },

  async getTickets(appId, params = {}) {
    const { data } = await client.get(`/api/helpdesk/apps/${appId}/tickets`, { params });
    return data;
  },

  async getTicket(appId, ticketNumber) {
    const { data } = await client.get(`/api/helpdesk/apps/${appId}/tickets/${ticketNumber}`);
    return data;
  },

  async updateTicket(appId, ticketNumber, ticketData) {
    const { data } = await client.patch(
      `/api/helpdesk/apps/${appId}/tickets/${ticketNumber}`,
      ticketData
    );
    return data;
  },

  async deleteTicket(appId, ticketNumber) {
    const { data } = await client.delete(`/api/helpdesk/apps/${appId}/tickets/${ticketNumber}`);
    return data;
  },

  async addComment(appId, ticketNumber, commentData) {
    const { data } = await client.post(
      `/api/helpdesk/apps/${appId}/tickets/${ticketNumber}/comments`,
      commentData
    );
    return data;
  },

  async updateComment(appId, ticketNumber, commentId, commentData) {
    const { data } = await client.patch(
      `/api/helpdesk/apps/${appId}/tickets/${ticketNumber}/comments/${commentId}`,
      commentData
    );
    return data;
  },

  async uploadAttachment(appId, ticketNumber, formData) {
    const { data } = await client.post(
      `/api/helpdesk/apps/${appId}/tickets/${ticketNumber}/attachments`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
    return data;
  },

  async deleteAttachment(appId, ticketNumber, attachmentId) {
    const { data } = await client.delete(
      `/api/helpdesk/apps/${appId}/tickets/${ticketNumber}/attachments/${attachmentId}`
    );
    return data;
  },

  async assignTicket(appId, ticketNumber, assignee) {
    const { data } = await client.post(
      `/api/helpdesk/apps/${appId}/tickets/${ticketNumber}/assign`,
      { assignee }
    );
    return data;
  },

  async createTaskFromTicket(appId, ticketNumber, overrides = {}) {
    const { data } = await client.post(
      `/api/helpdesk/apps/${appId}/tickets/${ticketNumber}/create-task`,
      overrides
    );
    return data;
  },

  async triageTicket(appId, ticketNumber) {
    const { data } = await client.post(
      `/api/helpdesk/apps/${appId}/tickets/${ticketNumber}/triage`
    );
    return data;
  },

  async refreshTriage(appId, ticketNumber) {
    const { data } = await client.post(
      `/api/helpdesk/apps/${appId}/tickets/${ticketNumber}/triage/refresh`
    );
    return data;
  },

  async createTaskFromTriage(appId, ticketNumber) {
    const { data } = await client.post(
      `/api/helpdesk/apps/${appId}/tickets/${ticketNumber}/triage/create-task`
    );
    return data;
  },

  // ==================== UMBRELLA ====================
  async getUmbrellaTickets() {
    const { data } = await client.get('/api/helpdesk/apps/umbrella-tickets');
    return data;
  },

  async linkToUmbrella(appId, ticketNumber, umbrellaTicketId) {
    const { data } = await client.post(
      `/api/helpdesk/apps/${appId}/tickets/${ticketNumber}/umbrella`,
      { umbrellaTicketId }
    );
    return data;
  },

  async addChildToUmbrella(appId, ticketNumber, childTicketId) {
    const { data } = await client.post(
      `/api/helpdesk/apps/${appId}/tickets/${ticketNumber}/umbrella/add-child`,
      { childTicketId }
    );
    return data;
  },

  async getLinkableTickets() {
    const { data } = await client.get('/api/helpdesk/apps/linkable-tickets');
    return data;
  },

  async unlinkFromUmbrella(appId, ticketNumber) {
    const { data } = await client.delete(
      `/api/helpdesk/apps/${appId}/tickets/${ticketNumber}/umbrella`
    );
    return data;
  },

  // ==================== TASK LINKS ====================
  async getTaskLinks(appId, ticketNumber) {
    const { data } = await client.get(
      `/api/helpdesk/apps/${appId}/tickets/${ticketNumber}/task-links`
    );
    return data;
  },

  async createTaskLink(appId, ticketNumber, taskKey) {
    const { data } = await client.post(
      `/api/helpdesk/apps/${appId}/tickets/${ticketNumber}/task-links`,
      { taskKey }
    );
    return data;
  },

  async deleteTaskLink(appId, ticketNumber, linkId) {
    const { data } = await client.delete(
      `/api/helpdesk/apps/${appId}/tickets/${ticketNumber}/task-links/${linkId}`
    );
    return data;
  },

  // ==================== CONVERSATION LINKS ====================
  async getConversationLinks(appId, ticketNumber) {
    const { data } = await client.get(
      `/api/helpdesk/apps/${appId}/tickets/${ticketNumber}/conversation-links`
    );
    return data;
  },

  async createConversationLink(appId, ticketNumber, conversationId) {
    const { data } = await client.post(
      `/api/helpdesk/apps/${appId}/tickets/${ticketNumber}/conversation-links`,
      { conversationId }
    );
    return data;
  },

  async deleteConversationLink(appId, ticketNumber, linkId) {
    const { data } = await client.delete(
      `/api/helpdesk/apps/${appId}/tickets/${ticketNumber}/conversation-links/${linkId}`
    );
    return data;
  },

  async searchTickets(query, projectId, limit = 10) {
    const params = { query, limit };
    if (projectId) params.projectId = projectId;
    const { data } = await client.get('/api/helpdesk/tickets/search', { params });
    return data;
  },

  // ==================== CATEGORIES ====================
  async getCategories(appId) {
    const { data } = await client.get(`/api/helpdesk/apps/${appId}/categories`);
    return data;
  },

  async addCategory(appId, categoryData) {
    const { data } = await client.post(`/api/helpdesk/apps/${appId}/categories`, categoryData);
    return data;
  },

  async updateCategory(appId, slug, categoryData) {
    const { data } = await client.patch(
      `/api/helpdesk/apps/${appId}/categories/${slug}`,
      categoryData
    );
    return data;
  },

  async removeCategory(appId, slug) {
    const { data } = await client.delete(`/api/helpdesk/apps/${appId}/categories/${slug}`);
    return data;
  },
};
